/**
 * components/procurement/RateModelingDialog.jsx — per-period rate grid.
 *
 * For the active scenario: edit supply/delivery rates per year per commodity,
 * pull rates from linked contracts or live market feeds, and import/export a
 * CSV. On save it writes the grid into the scenario's `rate_overrides`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Upload, Loader2, Zap, LineChart } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import {
  patchScenario, getRateSources, uploadRateCsv, downloadRateTemplate,
} from '../../api/budgets';

const addMonths = (ym, n) => {
  let y = parseInt(ym.slice(0, 4), 10);
  let m = parseInt(ym.slice(5, 7), 10) + n;
  y += Math.floor((m - 1) / 12);
  m = ((m - 1) % 12) + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
};

export function RateModelingDialog({ open, onOpenChange, accountId, budgetId, scenario, budget, onSaved }) {
  const commodities = (budget?.commodities && budget.commodities.length)
    ? budget.commodities : ['electricity', 'gas', 'water'];
  const years = Math.max(1, Math.ceil((budget?.horizon_months || 12) / 12));
  const startMonth = budget?.start_month || '2026-01';

  const [commodity, setCommodity] = useState(commodities[0]);
  const [grid, setGrid] = useState([]); // [{supply, delivery}] per year
  const [sources, setSources] = useState({ contracts: [], market_feeds: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // year period windows
  const periods = useMemo(() => Array.from({ length: years }, (_, i) => ({
    from: addMonths(startMonth, i * 12),
    to: addMonths(startMonth, i * 12 + 11),
  })), [years, startMonth]);

  useEffect(() => {
    if (!open) return;
    getRateSources({ accountId }).then(setSources).catch(() => {});
  }, [open, accountId]);

  // seed grid from existing rate_overrides for the selected commodity
  useEffect(() => {
    if (!open) return;
    const ovs = (scenario?.assumptions?.rate_overrides || []).filter(
      (o) => !o.commodity || o.commodity === commodity);
    const next = periods.map((p) => {
      const match = ovs.find((o) => o.period_from === p.from) || ovs.find((o) => !o.period_from);
      return {
        supply: match?.supply_rate != null ? String(match.supply_rate) : '',
        delivery: match?.delivery_rate != null ? String(match.delivery_rate) : '',
      };
    });
    setGrid(next);
  }, [open, commodity, scenario, periods]);

  const setCell = (i, key, val) => setGrid((g) => g.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const fillSupply = (val) => setGrid((g) => g.map((r) => ({ ...r, supply: String(val) })));

  const save = async () => {
    setSaving(true);
    // overrides for OTHER commodities are preserved; this commodity's rows replaced
    const others = (scenario?.assumptions?.rate_overrides || []).filter(
      (o) => o.commodity && o.commodity !== commodity);
    const mine = grid
      .map((r, i) => ({
        commodity,
        period_from: periods[i].from,
        period_to: periods[i].to,
        supply_rate: r.supply === '' ? null : Number(r.supply),
        delivery_rate: r.delivery === '' ? null : Number(r.delivery),
      }))
      .filter((o) => o.supply_rate != null || o.delivery_rate != null);
    const assumptions = { ...(scenario.assumptions || {}), rate_overrides: [...others, ...mine] };
    try {
      await patchScenario({ accountId, id: budgetId, scenarioId: scenario.id, patch: { assumptions } });
      toast.success('Rate grid saved');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadRateCsv({ accountId, id: budgetId, scenarioId: scenario.id, file, mode: 'replace' });
      toast.success(`Imported ${res.imported} rate rows`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error('CSV import failed', { description: err?.response?.data?.detail || err.message });
    } finally { setUploading(false); e.target.value = ''; }
  };

  const contractsForCommodity = sources.contracts.filter((c) => c.commodity === commodity && c.rate != null);
  const feedsForCommodity = sources.market_feeds.filter((f) => f.commodity === commodity);

  if (!scenario) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rate-modeling-dialog">
        <DialogHeader>
          <DialogTitle>Model rates — {scenario.name}</DialogTitle>
          <DialogDescription>
            Set supply &amp; delivery rates per year, pull from a contract or the market, or import a CSV.
            Baseline rates from history are used wherever you leave a cell blank.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Commodity</Label>
              <Select value={commodity} onValueChange={setCommodity}>
                <SelectTrigger className="w-40" data-testid="rate-commodity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {commodities.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {contractsForCommodity.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><Zap className="w-3 h-3" /> Pull from contract</Label>
                <Select onValueChange={(id) => { const c = contractsForCommodity.find((x) => x.id === id); if (c) { fillSupply(c.rate); toast.message(`Applied ${c.name} supply rate ${c.rate}`); } }}>
                  <SelectTrigger className="w-52" data-testid="rate-pull-contract"><SelectValue placeholder="Select contract" /></SelectTrigger>
                  <SelectContent>
                    {contractsForCommodity.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.rate}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {feedsForCommodity.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><LineChart className="w-3 h-3" /> Pull from market</Label>
                <Select onValueChange={(sym) => { const f = feedsForCommodity.find((x) => x.symbol === sym); if (f) { fillSupply(f.latest_value); toast.message(`Applied ${f.name} ${f.latest_value}/${f.unit}`); } }}>
                  <SelectTrigger className="w-52" data-testid="rate-pull-market"><SelectValue placeholder="Select feed" /></SelectTrigger>
                  <SelectContent>
                    {feedsForCommodity.map((f) => <SelectItem key={f.symbol} value={f.symbol}>{f.name} — {f.latest_value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* per-year grid */}
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-2">Period</th>
                  <th className="text-left p-2">Supply rate ($/unit)</th>
                  <th className="text-left p-2">Delivery rate ($/unit)</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, i) => (
                  <tr key={p.from} className="border-t" data-testid={`rate-row-${i}`}>
                    <td className="p-2 whitespace-nowrap">Y{i + 1} <span className="text-muted-foreground text-xs">{p.from} → {p.to}</span></td>
                    <td className="p-2"><Input type="number" step="0.001" value={grid[i]?.supply ?? ''} placeholder="baseline" onChange={(e) => setCell(i, 'supply', e.target.value)} data-testid={`rate-supply-${i}`} /></td>
                    <td className="p-2"><Input type="number" step="0.001" value={grid[i]?.delivery ?? ''} placeholder="baseline" onChange={(e) => setCell(i, 'delivery', e.target.value)} data-testid={`rate-delivery-${i}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2 flex-wrap text-sm">
            <Button variant="outline" size="sm" onClick={() => downloadRateTemplate({ accountId })} data-testid="rate-download-template">
              <Download className="w-3.5 h-3.5 mr-1.5" /> CSV template
            </Button>
            <label className="inline-flex">
              <input type="file" accept=".csv" className="hidden" onChange={onUpload} data-testid="rate-upload-input" />
              <Button variant="outline" size="sm" asChild>
                <span className="cursor-pointer">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />} Import CSV (replaces all)
                </span>
              </Button>
            </label>
            <span className="text-xs text-muted-foreground ml-auto">CSV import replaces the full rate grid across commodities.</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rate-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : 'Save Rate Grid'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
